import React, { CSSProperties, useEffect, useState } from "react";
import { Canvas } from "@react-three/fiber";
import * as THREE from "three";
import { OrbitControls } from "@react-three/drei";
import Model from "~/Model";
import OutputText from "~/voice_utils/TextOutput";
import RubyText from "~/components/utils/RubyText";
import ThoughtBubble from "~/components/animation/ThoughtBubble";
import FaceDetection from "~/components/FaceDetection";
import Indicator from "~/components/Indicator";

interface ModelViewLayout3Props {
  visible: boolean;
  handleModelViewed: () => void;
  name: string;
  characterIndex: number;
  maxIndex: number;
  isSoundOn: boolean;
  toggleMute: () => void;
  isFaceDetection: boolean;
  setPrevCharacter: () => void | Promise<void>;
  setNextCharacter: () => void | Promise<void>;
  changeButtonsDisabled: boolean;
}

/**
 * 3Dモデルと関連UIを表示するコンポーネント
 */
// レンダラー設定やカメラ・ライト設定（UI 固定値）はローカルに保持
const canvasStyle: CSSProperties = {
  borderRadius: "10px",
  display: "block",
  height: "60% !important",
};

const cameraSettings = {
  fov: 28,
  near: 0.1,
  far: 100,
  position: { x: 0, y: 0.025, z: 3.0 },
};

const lightSettings = {
  directional: {
    position: { x: 0, y: 10, z: 10 },
    color: 0xffffff,
    intensity: 3.0,
  },
  ambient: { color: 0xffffff, intensity: 0.5 },
};

const glConfig = {
  antialias: true,
  alpha: true,
  outputColorSpace: THREE.SRGBColorSpace,
  toneMapping: THREE.LinearToneMapping,
  toneMappingExposure: 1.0,
};

const ModelViewLayout3: React.FC<ModelViewLayout3Props> = ({
  visible,
  handleModelViewed,
  name,
  characterIndex,
  maxIndex,
  isSoundOn,
  toggleMute,
  isFaceDetection,
  setPrevCharacter,
  setNextCharacter,
  changeButtonsDisabled,
}) => {
  // 可変項目のみカメラ設定をstate化
  const [cameraFov, setCameraFov] = useState(cameraSettings.fov);
  const [cameraPositionY, setCameraPositionY] = useState(cameraSettings.position.y);
  const [canvasKey, setCanvasKey] = useState(0); // Canvas再マウント用

  /**
   * ビューポートが縦向きの時にモデルのカメラ設定(fov/Y位置)を調整する
   */
  useEffect(() => {
    // 初期値を保存
    const originalFov = cameraSettings.fov;
    const originalPositionY = cameraSettings.position.y;

    // リサイズ時にfovとY位置を更新
    const updateFovAndPosition = () => {
      if (typeof window === "undefined") return;
      const isPortrait = window.innerHeight > window.innerWidth;
      const portraitCSFov = 33; // 縦向き時のfov値
      const portraitCSPositionY = 0.1; // 縦向き時のカメラY位置
      if (isPortrait) {
        setCameraFov(portraitCSFov);
        setCameraPositionY(portraitCSPositionY);
      } else {
        setCameraFov(originalFov);
        setCameraPositionY(originalPositionY);
      }
      // Canvasを再マウントしてcameraプロパティを反映させる
      setCanvasKey((prev) => prev + 1);
    };

    updateFovAndPosition();
    window.addEventListener("resize", updateFovAndPosition);
    return () => window.removeEventListener("resize", updateFovAndPosition);
  }, []);

  return (
    <div className="character-area-layout-3">
      <OutputText />

      <div className="chatpage-canvas-layout-3">
        <Canvas
          key={canvasKey}
          className={`main-canvas-layout-3 ${visible ? "visible" : "hide"}`}
          // カメラ設定の使用箇所
          camera={{
            fov: cameraFov,
            near: cameraSettings.near,
            far: cameraSettings.far,
            position: [cameraSettings.position.x, cameraPositionY, cameraSettings.position.z],
          }}
          flat
          style={canvasStyle}
          gl={glConfig}
        >
          {/* ライト設定の使用箇所 */}
          {/* eslint-disable react/no-unknown-property */}
          <directionalLight
            position-x={lightSettings.directional.position.x}
            position-y={lightSettings.directional.position.y}
            position-z={lightSettings.directional.position.z}
            color={lightSettings.directional.color}
            intensity={lightSettings.directional.intensity}
          />
          <OrbitControls enableZoom={false} enableRotate={false} enablePan={false} />
          <ambientLight
            color={lightSettings.ambient.color}
            intensity={lightSettings.ambient.intensity}
          />
          <Model onViewed={handleModelViewed} />
        </Canvas>
        <ThoughtBubble />
      </div>
      <div className="volume-btn-layout-3">
        <button
          className={`btn btn-circle ${isSoundOn ? "sound-on" : "sound-off"}`}
          onClick={toggleMute}
        >
          {isSoundOn ? (
            <span className="material-symbols-rounded sound-on-btn">volume_up</span>
          ) : (
            <span className="material-symbols-rounded sound-off-btn">volume_off</span>
          )}
        </button>
      </div>
      <div className="character-name">
        <RubyText text={name} />
      </div>

      {/*効率化のためisFaceDetectionがOFFなら切る*/}
      {isFaceDetection && <FaceDetection />}
      <div className="character-button-layout-3">
        <div className="change-btn">
          <button
            className="btn btn-circle"
            onMouseUp={() => void setPrevCharacter()}
            disabled={changeButtonsDisabled}
          >
            <span className="material-symbols-rounded">keyboard_arrow_left</span>
          </button>
          <div className="indicator horizontal-only">
            <Indicator
              characterIndex={characterIndex}
              maxIndex={maxIndex}
              className={"indicator-list-layout-3"}
            />
          </div>
          <div className="indicator vertical-only">
            <Indicator
              characterIndex={characterIndex}
              maxIndex={maxIndex}
              className={"indicator-list-layout-3"}
            />
          </div>
          <button
            className="btn btn-circle"
            onMouseUp={() => void setNextCharacter()}
            disabled={changeButtonsDisabled}
          >
            <span className="material-symbols-rounded">keyboard_arrow_right</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModelViewLayout3;
